import { Location } from '@angular/common';
import { computed, inject, Injectable, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { PreviewTarget } from './preview.model';
import { PREVIEW_VIEWS } from './preview.views';

const PARAM = 'preview';

/** How a view asks for another preview, such as a trip picked from a list. */
export interface PreviewNavigator {
    open(target: PreviewTarget): void;
}

// A view placed straight on a page opens the panel. The panel provides its own, which stacks.
export const PREVIEW_NAVIGATOR = new InjectionToken<PreviewNavigator>('PREVIEW_NAVIGATOR', {
    providedIn: 'root',
    factory: () => {
        const previews = inject(PreviewService);
        return { open: target => previews.open(target) };
    },
});

// Each segment is a kind followed by its params in the order the view declares them, with trailing
// empty params left off. Values are ids and ISO dates, which never contain either separator, and
// Angular leaves both unescaped.
function formatStack(stack: PreviewTarget[]): string | null {
    return stack.map(target => {
        const values = [target.kind, ...PREVIEW_VIEWS[target.kind].params.map(name => target.params[name] ?? '')];
        while (!values[values.length - 1]) {
            values.pop();
        }
        return values.join(',');
    }).join(';') || null;
}

function parseStack(text: string | null): PreviewTarget[] {
    return (text ? text.split(';') : [])
        .map(segment => segment.split(','))
        .filter(([kind]) => !!PREVIEW_VIEWS[kind])
        .map(([kind, ...values]) => ({
            kind,
            params: Object.fromEntries(PREVIEW_VIEWS[kind].params.map((name, index) => [name, values[index]])),
        }));
}

// The previews open in the side panel, held in the URL so a reload or a shared link reopens them. A
// view opened from inside the panel is a history entry of its own, so the browser's back closes it.
@Injectable({ providedIn: 'root' })
export class PreviewService {

    private readonly location = inject(Location);
    private readonly router = inject(Router);

    private readonly queryParams = toSignal(inject(ActivatedRoute).queryParamMap, { requireSync: true });
    private readonly param = computed(() => this.queryParams().get(PARAM));

    /** The open previews, the one showing last. Empty when the panel is closed. */
    readonly stack = computed(() => parseStack(this.param()));

    /** Open the panel on the target alone, replacing whatever it held. */
    open(target: PreviewTarget): void {
        this.navigate([target]);
    }

    /** Show the target over the current preview, which back returns to. */
    push(target: PreviewTarget): void {
        this.navigate([...this.stack(), target], { state: { previewPushed: true } });
    }

    // A preview that was pushed has the one under it as the history entry before it. One opened
    // from a link has nothing to go back to in history, so it is replaced instead.
    back(): void {
        if ((this.location.getState() as { previewPushed?: boolean })?.previewPushed) {
            this.location.back();
        } else {
            this.navigate(this.stack().slice(0, -1), { replaceUrl: true });
        }
    }

    close(): void {
        this.navigate([]);
    }

    private navigate(stack: PreviewTarget[], extras: NavigationExtras = {}): void {
        this.router.navigate([], { queryParams: { [PARAM]: formatStack(stack) }, queryParamsHandling: 'merge', ...extras });
    }
}
